import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import { RefreshCw, X, Edit2, Filter, Search, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Container, Row, Col, Card, Table, Form, InputGroup, Button, Badge, Spinner } from 'react-bootstrap';
import { API_CONFIG } from '../services/config';
import { ALL_BROKERS, BROKER_LABELS, BROKER_BADGE_VARIANT, BROKER_BADGE_TEXT, BrokerName } from '../services/brokerApi';

interface Order {
  id: number;
  order_id: string;
  trading_symbol: string;
  transaction_type: string;
  order_status: string;
  quantity: number;
  price: number;
  filled_qty: number;
  order_type: string;
  product_type: string;
  create_time: string;
  update_time: string;
  exchange_segment: string;
  validity: string;
  trigger_price: number;
  security_id: string;
  broker?: string;
  oms_error_description?: string | null;
  internal_error?: string | null;
}

const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [brokerFilter, setBrokerFilter] = useState<string>('ALL');
  const [clearing, setClearing] = useState(false);
  const [clearProgress, setClearProgress] = useState(0);
  const [showClearModal, setShowClearModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [deletedCount, setDeletedCount] = useState(0);
  const [deleteType, setDeleteType] = useState<'soft' | 'hard'>('soft');

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, statusFilter, typeFilter, brokerFilter]);

  const fetchOrders = async (fetchFromDhan = false) => {
    setLoading(true);
    setError('');
    try {
      const endpoint = fetchFromDhan ? '/orders/dhan' : '/orders?page=1&limit=100';
      const url = `${API_CONFIG.baseURL}${endpoint}`;
      console.log('[OrdersPage] Fetching from:', url);

      const token = localStorage.getItem('token');
      console.log('[OrdersPage] Token:', token ? 'exists' : 'missing');

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('[OrdersPage] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[OrdersPage] Error response:', errorData);
        throw new Error(errorData.error || 'Failed to fetch orders');
      }

      const data = await response.json();
      console.log('[OrdersPage] Response data:', data);

      const ordersList = fetchFromDhan ? data : data.data;
      console.log('[OrdersPage] Orders list:', ordersList);

      setOrders(ordersList);
    } catch (err: any) {
      console.error('[OrdersPage] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];

    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.trading_symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.order_id.includes(searchTerm)
      );
    }

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(order => order.order_status === statusFilter);
    }

    if (typeFilter !== 'ALL') {
      filtered = filtered.filter(order => order.transaction_type === typeFilter);
    }

    if (brokerFilter !== 'ALL') {
      filtered = filtered.filter(order => (order.broker || 'DHAN') === brokerFilter);
    }

    setFilteredOrders(filtered);
  };

  const handleCancelOrder = async (orderId: string) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    alert(`Cancel order ${orderId} - API integration pending`);
  };

  const handleModifyOrder = (orderId: string) => {
    alert(`Modify order ${orderId} - API integration pending`);
  };

  const handleClearHistory = async () => {
    setShowClearModal(false);
    setClearing(true);
    setClearProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setClearProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const response = await fetch(`${API_CONFIG.baseURL}/orders/clear`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      clearInterval(progressInterval);
      setClearProgress(100);

      if (!response.ok) throw new Error('Failed to clear order history');

      const data = await response.json();

      setTimeout(() => {
        setOrders([]);
        setFilteredOrders([]);
        setClearing(false);
        setClearProgress(0);
        setDeletedCount(data.deletedCount);
        setDeleteType('soft');
        setShowSuccessModal(true);
      }, 500);
    } catch (err: any) {
      setError(err.message);
      setClearing(false);
      setClearProgress(0);
    }
  };

  const handleDeletePermanently = async () => {
    setShowDeleteModal(false);
    setClearing(true);
    setClearProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setClearProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const response = await fetch(`${API_CONFIG.baseURL}/orders/delete-permanently`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      clearInterval(progressInterval);
      setClearProgress(100);

      if (!response.ok) throw new Error('Failed to delete orders permanently');

      const data = await response.json();

      setTimeout(() => {
        setOrders([]);
        setFilteredOrders([]);
        setClearing(false);
        setClearProgress(0);
        setDeletedCount(data.deletedCount);
        setDeleteType('hard');
        setShowSuccessModal(true);
      }, 500);
    } catch (err: any) {
      setError(err.message);
      setClearing(false);
      setClearProgress(0);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'TRADED':
      case 'EXECUTED': return <CheckCircle size={14} />;
      case 'REJECTED': return <XCircle size={14} />;
      case 'PENDING': return <Clock size={14} />;
      case 'CANCELLED': return <X size={14} />;
      default: return <AlertCircle size={14} />;
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'TRADED':
      case 'EXECUTED': return 'success';
      case 'REJECTED': return 'danger';
      case 'PENDING': return 'warning';
      case 'CANCELLED': return 'secondary';
      default: return 'info';
    }
  };

  const stats = {
    total: orders.length,
    executed: orders.filter(o => o.order_status === 'EXECUTED' || o.order_status === 'TRADED').length,
    pending: orders.filter(o => o.order_status === 'PENDING').length,
    rejected: orders.filter(o => o.order_status === 'REJECTED').length,
    traded: orders.filter(o => (o.order_status === 'EXECUTED' || o.order_status === 'TRADED') && o.filled_qty > 0).length,
  };

  return (
    <MainLayout>
      <Container fluid className="py-3" style={{ background: '#f8f9fa', minHeight: '100vh' }}>
        {/* Header */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1" style={{ fontWeight: '700', fontSize: '24px' }}>Order Book</h2>
              <p className="text-muted mb-0" style={{ fontSize: '13px' }}>Manage and track your trading orders</p>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="warning"
                onClick={() => setShowClearModal(true)}
                disabled={loading || clearing || orders.length === 0}
                style={{ fontSize: '13px', fontWeight: '500' }}
              >
                <X size={16} className="me-2" />
                Clear History
              </Button>
              <Button
                variant="danger"
                onClick={() => setShowDeleteModal(true)}
                disabled={loading || clearing || orders.length === 0}
                style={{ fontSize: '13px', fontWeight: '500' }}
              >
                <X size={16} className="me-2" />
                Delete Permanently
              </Button>
            </div>
          </div>

          {/* Progress Bar */}
          {clearing && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span style={{ fontSize: '13px', fontWeight: '500' }}>Clearing order history...</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#0d6efd' }}>{clearProgress}%</span>
              </div>
              <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${clearProgress}%`, background: '#0d6efd' }}
                  aria-valuenow={clearProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </div>
          )}

          {/* Stats Cards */}
          <Row className="g-3">
            <Col md={2}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-3">
                  <div className="text-muted" style={{ fontSize: '12px', fontWeight: '500' }}>Total Orders</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#212529' }}>{stats.total}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-3">
                  <div className="text-muted" style={{ fontSize: '12px', fontWeight: '500' }}>Traded</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#0d6efd' }}>{stats.traded}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-3">
                  <div className="text-muted" style={{ fontSize: '12px', fontWeight: '500' }}>Executed</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#28a745' }}>{stats.executed}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-3">
                  <div className="text-muted" style={{ fontSize: '12px', fontWeight: '500' }}>Pending</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#ffc107' }}>{stats.pending}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-3">
                  <div className="text-muted" style={{ fontSize: '12px', fontWeight: '500' }}>Rejected</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#dc3545' }}>{stats.rejected}</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {error && (
          <div className="alert alert-danger d-flex align-items-center mb-3" role="alert">
            <AlertCircle size={20} className="me-2" />
            {error}
          </div>
        )}

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-3">
          <Card.Body className="p-3">
            <Row className="g-2">
              <Col md={4}>
                <InputGroup size="sm">
                  <InputGroup.Text style={{ background: 'white' }}>
                    <Search size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by symbol or order ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ fontSize: '13px' }}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <InputGroup size="sm">
                  <InputGroup.Text style={{ background: 'white' }}>
                    <Filter size={16} />
                  </InputGroup.Text>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ fontSize: '13px' }}
                  >
                    <option value="ALL">All Status</option>
                    <option value="TRADED">Traded</option>
                    <option value="EXECUTED">Executed</option>
                    <option value="PENDING">Pending</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="CANCELLED">Cancelled</option>
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col md={2}>
                <Form.Select
                  size="sm"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  style={{ fontSize: '13px' }}
                >
                  <option value="ALL">All Types</option>
                  <option value="BUY">Buy</option>
                  <option value="SELL">Sell</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select
                  size="sm"
                  value={brokerFilter}
                  onChange={(e) => setBrokerFilter(e.target.value)}
                  style={{ fontSize: '13px' }}
                >
                  <option value="ALL">All Brokers</option>
                  {ALL_BROKERS.map(b => (
                    <option key={b} value={b}>{BROKER_LABELS[b]}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Orders Table */}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <div style={{ overflowX: 'auto' }}>
              <Table hover responsive className="mb-0" style={{ fontSize: '13px' }}>
                <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <tr>
                    <th style={{ fontWeight: '600', fontSize: '11px', padding: '12px' }}>TIME</th>
                    <th style={{ fontWeight: '600', fontSize: '11px', padding: '12px' }}>SYMBOL</th>
                    <th style={{ fontWeight: '600', fontSize: '11px', padding: '12px' }}>TYPE</th>
                    <th style={{ fontWeight: '600', fontSize: '11px', padding: '12px' }}>STATUS</th>
                    <th style={{ fontWeight: '600', fontSize: '11px', padding: '12px', textAlign: 'right' }}>QTY</th>
                    <th style={{ fontWeight: '600', fontSize: '11px', padding: '12px', textAlign: 'right' }}>FILLED</th>
                    <th style={{ fontWeight: '600', fontSize: '11px', padding: '12px', textAlign: 'right' }}>PRICE</th>
                    <th style={{ fontWeight: '600', fontSize: '11px', padding: '12px', textAlign: 'right' }}>TRIGGER</th>
                    <th style={{ fontWeight: '600', fontSize: '11px', padding: '12px' }}>ORDER TYPE</th>
                    <th style={{ fontWeight: '600', fontSize: '11px', padding: '12px' }}>VALIDITY</th>
                    <th style={{ fontWeight: '600', fontSize: '11px', padding: '12px' }}>SECURITY ID</th>
                    <th style={{ fontWeight: '600', fontSize: '11px', padding: '12px' }}>BROKER</th>
                    <th style={{ fontWeight: '600', fontSize: '11px', padding: '12px', textAlign: 'right' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="text-center py-5">
                        <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                        Loading orders...
                      </td>
                    </tr>
                  ) : filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="text-center py-5 text-muted">
                        <AlertCircle size={32} className="mb-2 d-block mx-auto" style={{ opacity: 0.5 }} />
                        No orders found
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order) => (
                      <tr key={order.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', color: '#6c757d' }}>
                          {order.create_time ? new Date(order.create_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontWeight: '600', color: '#212529' }}>{order.trading_symbol}</div>
                          <div style={{ fontSize: '11px', color: '#6c757d' }}>{order.exchange_segment}</div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{ fontWeight: '600', color: order.transaction_type === 'BUY' ? '#28a745' : '#dc3545' }}>
                            {order.transaction_type}
                          </span>
                        </td>
                        <td style={{ padding: '12px', maxWidth: '220px' }}>
                          <Badge
                            bg={getStatusVariant(order.order_status)}
                            className="d-inline-flex align-items-center gap-1"
                            style={{ fontSize: '11px', fontWeight: '500' }}
                          >
                            {getStatusIcon(order.order_status)}
                            {order.order_status}
                          </Badge>
                          {order.order_status === 'REJECTED' && (order.oms_error_description || order.internal_error) && (
                            <div
                              title={order.oms_error_description || order.internal_error || ''}
                              style={{
                                fontSize: '10px', color: '#dc3545', marginTop: '3px',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help'
                              }}
                            >
                              {order.oms_error_description || order.internal_error}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: '500' }}>{order.quantity}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#6c757d' }}>{order.filled_qty}</td>
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: '500' }}>₹{Number(order.price).toFixed(2)}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#6c757d' }}>
                          {order.trigger_price > 0 ? `₹${Number(order.trigger_price).toFixed(2)}` : '-'}
                        </td>
                        <td style={{ padding: '12px', color: '#6c757d' }}>{order.order_type}</td>
                        <td style={{ padding: '12px', color: '#6c757d' }}>{order.validity}</td>
                        <td style={{ padding: '12px', color: '#6c757d' }}>{order.security_id}</td>
                        <td style={{ padding: '12px' }}>
                          <Badge bg={BROKER_BADGE_VARIANT[(order.broker || 'DHAN') as BrokerName]} text={BROKER_BADGE_TEXT[(order.broker || 'DHAN') as BrokerName]} style={{ fontSize: '10px' }}>
                            {order.broker || 'DHAN'}
                          </Badge>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          <div className="d-flex justify-content-end gap-1">
                            {order.order_status === 'PENDING' && (
                              <>
                                <Button
                                  variant="link"
                                  size="sm"
                                  onClick={() => handleModifyOrder(order.order_id)}
                                  className="p-1 text-primary"
                                  title="Modify Order"
                                >
                                  <Edit2 size={16} />
                                </Button>
                                <Button
                                  variant="link"
                                  size="sm"
                                  onClick={() => handleCancelOrder(order.order_id)}
                                  className="p-1 text-danger"
                                  title="Cancel Order"
                                >
                                  <X size={16} />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* Results Count */}
        {filteredOrders.length > 0 && (
          <div className="text-center mt-3 text-muted" style={{ fontSize: '13px' }}>
            Showing {filteredOrders.length} of {orders.length} orders
          </div>
        )}
      </Container>

      {/* Clear History Confirmation Modal */}
      {showClearModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#ffc107', marginBottom: '8px' }}>
                Clear Order History?
              </h3>
              <p style={{ fontSize: '14px', color: '#6c757d', margin: 0 }}>
                This will hide {orders.length} orders from view (soft delete). Orders remain in database.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowClearModal(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  backgroundColor: 'white',
                  color: '#374151',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleClearHistory}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  backgroundColor: '#ffc107',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Clear History
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Permanently Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#dc3545', marginBottom: '8px' }}>
                Delete Orders Permanently?
              </h3>
              <p style={{ fontSize: '14px', color: '#6c757d', margin: 0 }}>
                This will permanently delete {orders.length} orders from the database. This action cannot be undone!
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  backgroundColor: 'white',
                  color: '#374151',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePermanently}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '400px',
            padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <CheckCircle size={48} style={{ color: '#28a745', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#212529', marginBottom: '8px' }}>
              {deleteType === 'soft' ? 'Successfully Cleared!' : 'Permanently Deleted!'}
            </h3>
            <p style={{ fontSize: '16px', color: '#6c757d', margin: 0 }}>
              {deletedCount} orders have been {deleteType === 'soft' ? 'hidden from view' : 'permanently deleted from database'}.
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              style={{
                marginTop: '20px',
                padding: '8px 24px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: '#28a745',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default OrdersPage;
